import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import {
  FileInput, Select,
  Button as MantineButton,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconFileUpload,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { ReactComponent as IconError } from "src/assets/icons/toast-error.svg";
import { ReactComponent as IconSuccess } from "src/assets/icons/toast-success.svg";
import { ReactComponent as IconWarning } from "src/assets/icons/toast-warning.svg";
import useCleverTapEvents from 'src/events/useGetEvents';
import { FNO_EVENT } from 'src/events/constant';
import { OnboardingProgress } from '../../components/onboardingProgress';
import { RootState } from '../../redux/store';
import { useStyles } from './incomeProofForm.styles';
import {
  saveFnoActivationData, setFnoActivationFormStatus,
  clearFnoActivationFormError,
} from './fnoActivationFormSlice';
import { getProgressStepper } from '../../utils/helpers';
import { clearPreviousStep } from '../account/accountSlice';
import { Button, TextInput } from '../../components/atoms';
import { ScreenLoader } from '../../components/screenLoader';

interface DocOptions {
  value: string;
  label: string;
}
interface IUploadDocumentFlow {
  handleChangeSetuFlow: () => void;
}
const UploadDocumentFlow = ({ handleChangeSetuFlow }: IUploadDocumentFlow) => {
  const { classes, cx } = useStyles();
  const fnoActivationState = useSelector((state: RootState) => state.fnoActivationDetails);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { TriggerEvent } = useCleverTapEvents();
  const currentStep = useSelector((state: RootState) => state.account.userDetails.currentStep);
  const account = useSelector((state: RootState) => state.account);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const fileRef: any = useRef(null);
  const [docType, setDocType] = useState<string | null>(null);
  const docOpts: Array<DocOptions> = [
    { value: 'bank statement', label: 'Bank statement' },
    { value: 'form 16', label: 'Form 16' },
    { value: 'dp holding', label: 'DP Holding' },
    { value: 'it return', label: 'IT Return' },
    { value: 'salary slip', label: 'Salary slip' },
    { value: 'net worth certificate', label: 'Net worth certificate' },
  ];
  const [errors, setErrors] = useState({
    fileError: '',
    selectError: '',
  });
  const [isPassRequired, setIsPassRequired] = useState(false);
  const [docPassword, setDocPassword] = useState('');
  const isLoading = useSelector((state: RootState) => state.fnoActivationDetails.status);

  const isButtonDisabled = () => {
    if (docType && documentFile) {
      if (isPassRequired && docPassword) {
        return false;
      } if (isPassRequired) {
        return true;
      } return false;
    }
    return true;
  };
  useEffect(() => {
    if (fnoActivationState.status === 'success') {
      dispatch(setFnoActivationFormStatus('idle'));
      if (fnoActivationState.fnoSkipped) {
        notifications.show({
          color: 'orange',
          message: 'Fno Trade Activation Skipped!',
          icon: <IconWarning />,
        });
      } else {
        notifications.show({
          color: 'green',
          message: 'Income Details Saved!',
          icon: <IconSuccess />,
        });
      }
      navigate('/aadhaar-esign');
    } else if (fnoActivationState.status === 'error') {
      TriggerEvent(FNO_EVENT.enableFnoError, {
        errorMessage: fnoActivationState.error.message || "",
        name: account.userDetails.name,
      });
      dispatch(clearFnoActivationFormError());
      notifications.show({
        color: 'red',
        message: fnoActivationState.error.message || 'Could\'nt save income details, Try again!',
        icon: <IconError />,
      });
    }
  }, [fnoActivationState.status]);

  const onDocTypeChange = (val: string | null) => {
    setDocType(val);
    setDocumentFile(null);
    setErrors({
      fileError: '',
      selectError: '',
    });
  };

  useEffect(() => {
    if (account.isStepChanged) {
      navigate('/add-nominee');
    }
    dispatch(clearPreviousStep(account));
  }, [account.isStepChanged]);
  const onDocumentChange = (val: File) => {
    setDocumentFile(val);
    fileRef.current = val;
    setErrors({
      fileError: '',
      selectError: '',
    });
  };

  const onNextClick = () => {
    if (!docType) {
      setErrors((prev) => ({ ...prev, selectError: 'must select document type to be uploaded' }));
    }
    if (!fileRef.current) {
      setErrors((prev) => ({ ...prev, fileError: 'must select document to be uploaded' }));
    }
    if (docType && fileRef.current) {
      dispatch(saveFnoActivationData({ fileData: documentFile, documentName: docType, password: docPassword }));
      TriggerEvent(FNO_EVENT.enableFnoManual, { incomeDocType: docType, name: account.userDetails.name });
    }
  };

  const handleChangePasswordRequired = () => {
    setIsPassRequired((prev) => !prev);
    setDocPassword('');
  };

  const handlePasswordInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPassRequired) {
      setDocPassword(e.target.value);
    } else {
      setDocPassword('');
    }
  };
  return (
    <>
      <div className="csMainContainer">
        <div className="csHeader">
          <div className="csProgressWrapper">
            <OnboardingProgress progress={getProgressStepper(currentStep)} />
          </div>
        </div>
        <div className={classes.contentContainer}>
          <h2 className="csHeading">Enable F&O trading</h2>
          <p className="csDescription">
            Upload any of the given documents as an income proof to enable F&O
          </p>
          <div className={classes.formWrapper}>
            <Select
              value={docType}
              onChange={onDocTypeChange}
              data={docOpts}
              placeholder="Select Document"
              error={errors.selectError}
            />
            {docType ? (
              <FileInput
                value={documentFile}
                onChange={onDocumentChange}
              // placeholder="Upload"
                accept="image/jpg,image/jpeg,image/png,.doc,.docx,.txt,.pdf"
                error={errors.fileError}
                classNames={{
                  wrapper: classes.fileInputWrapper,
                  placeholder: classes.fileInputPlaceholder,
                }}
                icon={<IconFileUpload stroke="1.2px" />}
              />
            ) : null}
            {documentFile && docType ? (
              <label htmlFor="scales" className={classes.checkLabel}>
                <input
                  type="checkbox"
                  id="isPoliticallyNotExposed"
                  name="isPoliticallyNotExposed"
                  checked={isPassRequired}
                  onChange={handleChangePasswordRequired}
                  className={classes.checkbox}
                />
                <p className={cx(classes.inputDescription, classes.terms)}>
                  Check if the Document is password protected
                </p>
              </label>
            ) : null}
            {isPassRequired ? (
              <TextInput
                type="password"
                name="password"
                placeholder="Enter the Document password here"
                autoComplete="off"
                required
                onChange={handlePasswordInput}
                value={docPassword}
              />
            ) : null}
          </div>
        </div>
        <div className="csFooter">
          <div className={classes.optionContainer}>
            <MantineButton
              radius="md"
              className="heading_2xs_semibold"
              variant="outline"
              onClick={handleChangeSetuFlow}
              leftIcon={<IconChevronLeft />}
            >
              Back
            </MantineButton>
            <Button disabled={isButtonDisabled()} onClick={onNextClick}>
              Next
            </Button>
          </div>
        </div>
      </div>
      {isLoading === 'loading' ? <ScreenLoader /> : null}
    </>
  );
};

export default UploadDocumentFlow;
